import React from "react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { CalendarClock, MessageCircle, Phone } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useApi } from "@/lib/useApi";
import { waLink } from "@/lib/format";
import { Card, EmptyState, ErrorState, ListSkeleton, PageTitle } from "@/components/common";
import { BackLink } from "@/pages/More";
import { VISIT_CLS, visitWhen } from "@/lib/visits";

function VisitCard({ v, act }: { v: any; act: (id: string, a: string) => void }) {
  return (
    <Card className="p-4" data-testid={`owner-visit-${v.id}`}>
      <div className="flex justify-between gap-2">
        <div className="min-w-0">
          <p className="text-sm font-bold text-[#3d52a0]" data-testid={`owner-visit-when-${v.id}`}>{visitWhen(v)}</p>
          <p className="font-semibold mt-0.5">{v.name}</p>
          <p className="text-xs text-slate-500">+91 {v.phone} · {v.email}</p>
        </div>
        <span data-testid={`owner-visit-status-${v.id}`} className={`h-fit text-xs font-semibold rounded-full border px-2.5 py-1 capitalize ${VISIT_CLS[v.status]}`}>{v.status}</span>
      </div>
      {v.note && <p className="text-sm text-slate-600 mt-2">"{v.note}"</p>}
      {["pending", "confirmed"].includes(v.status) && (
        <div className="flex gap-2 mt-3">
          <a href={`tel:+91${v.phone}`} aria-label="Call" data-testid={`owner-visit-call-${v.id}`} className="h-11 w-11 shrink-0 rounded-xl border border-slate-200 grid place-items-center"><Phone className="h-4 w-4" /></a>
          <a href={waLink(v.phone, `Hi ${v.name}, this is about your visit to ${v.property_name} on ${visitWhen(v)}.`)} target="_blank" rel="noreferrer" aria-label="WhatsApp" data-testid={`owner-visit-wa-${v.id}`} className="h-11 w-11 shrink-0 rounded-xl border border-slate-200 grid place-items-center text-[#128C4A]"><MessageCircle className="h-4 w-4" /></a>
          <Button data-testid={`owner-visit-decline-${v.id}`} variant="outline" onClick={() => act(v.id, "decline")} className="flex-1 h-11 rounded-xl text-rose-600 border-rose-200">Decline</Button>
          {v.status === "pending"
            ? <Button data-testid={`owner-visit-confirm-${v.id}`} onClick={() => act(v.id, "confirm")} className="flex-1 h-11 rounded-xl bg-emerald-700 hover:bg-emerald-800">Confirm</Button>
            : <Button data-testid={`owner-visit-done-${v.id}`} onClick={() => act(v.id, "done")} className="flex-1 h-11 rounded-xl bg-emerald-700 hover:bg-emerald-800">Mark done</Button>}
        </div>
      )}
    </Card>
  );
}

export default function Visits() {
  const { property } = useAuth();
  const { data, loading, error, reload } = useApi<any[]>(property ? `/properties/${property.id}/visits` : null);
  const act = async (id: string, a: string) => {
    if (a === "decline" && !window.confirm("Decline this visit? The resident will get an email.")) return;
    try {
      await api.post(`/visits/${id}/${a}`);
      toast.success(a === "confirm" ? "Visit confirmed. Resident notified by email." : a === "decline" ? "Visit declined" : "Marked as done");
      reload();
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div>
      <BackLink />
      <PageTitle title="Visits" sub={property?.name} />
      {loading && !data ? <ListSkeleton rows={3} /> : error ? <ErrorState message={error} onRetry={reload} /> : !data.length ? (
        <EmptyState testId="visits-empty" icon={CalendarClock} title="No visit requests yet" text="Residents can book a visit from your PG listing. You'll get an email for each request." />
      ) : (
        <div className="space-y-2.5 pg-stagger">{data.map((v) => <VisitCard key={v.id} v={v} act={act} />)}</div>
      )}
    </div>
  );
}
